import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { OrderingErrorCategory } from 'api/orderingErrorConfig'
import { ReactComponent as IconReset } from 'assets/images/icon-reset.svg'
import { ReactComponent as WandMagicSparkles } from 'assets/images/wand-magic-sparkles.svg'
import Badge from 'components/Badge'
import palette from 'constants/palette'
import { useErrorCategoryEditing } from 'hooks/useOrderingErrorMessages'
import { buildTranslate } from 'locales'
import { useRef } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useIsMedium } from 'utilities/responsive'
import { generateId } from 'utilities/uniqueId'
import AvailableVariables from './AvailableVariables'
import './ErrorMessages.scss'
import ExampleMessages from './ExampleMessages'
import PendingAiSuggestion from './PendingAiSuggestion'
import { REPORT_CATEGORY_MAPS } from './reportCategoryMap'

const t = buildTranslate('ordering_management.error_messages')

type Props = {
  category: OrderingErrorCategory
  providerName: string
  providerKey: string
  onGenerateAi: () => void
}

const ErrorCategoryCard = ({
  category,
  providerName,
  providerKey,
  onGenerateAi,
}: Props) => {
  const [css] = useStyletron()
  const isTabletOrSmaller = useIsMedium()
  const tooltipId = useRef(`report-category-tooltip-${generateId()}`).current

  const {
    customMessage,
    hasChanges,
    isGeneratingAi,
    pendingAiSuggestion,
    source,
    updateMessage,
    revert,
    acceptSuggestion,
    rejectSuggestion,
  } = useErrorCategoryEditing(category.id)

  const hasCustomMessage = customMessage !== null && customMessage !== ''

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateMessage(e.target.value)
  }

  const isAiSource = source === 'ai'
  const showPill = hasCustomMessage && source !== null

  const pillLabel = isAiSource
    ? t('pill_ai_personalized')
    : t('pill_customized')
  const pillBg = isAiSource ? palette.spearmint10 : palette.grey20
  const pillColor = isAiSource ? palette.spearmint50 : palette.grey70
  const inputBorderColor = isAiSource ? palette.spearmint30 : palette.grey40

  const reportCategory = REPORT_CATEGORY_MAPS[providerKey]?.[category.id]

  return (
    <div
      className={`p-m mb-s d-flex flex-column ${css({
        backgroundColor: palette.white,
        border: `1px solid ${palette.grey20}`,
        borderRadius: '0.25rem',
        gap: '1.5rem',
      })}`}
    >
      {/* Header row: provider label + messages (left) | report link (right) */}
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
          })}
        >
          <Text.BodyText4
            tag="span"
            color="grey90"
            className="font-weight-bold d-block"
          >
            {t('default_message_from_provider', { provider: providerName })}
          </Text.BodyText4>
          <ExampleMessages messages={category.example_messages} />
        </div>
        {reportCategory && !isTabletOrSmaller && (
          <Text.BodyText5
            tag="span"
            color="grey70"
            className={css({
              textAlign: 'right',
              flexShrink: 1,
            })}
          >
            {t('appears_as')}{' '}
            <span className={css({ color: palette.grey90 })}>
              &ldquo;{reportCategory}&rdquo;
            </span>
            {` ${t('in_the')} `}
            <Link
              to="/reports/ordering_conversion"
              className={css({
                color: palette.belizeHole,
                textDecoration: 'none',
              })}
            >
              {t('unaccepted_carts_report')}
            </Link>
          </Text.BodyText5>
        )}
        {reportCategory && isTabletOrSmaller && (
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id={tooltipId}>
                {t('appears_as_report', { category: reportCategory })}
              </Tooltip>
            }
          >
            <button
              type="button"
              className={css({
                background: 'none',
                border: 'none',
                padding: '0',
                cursor: 'help',
                flexShrink: 0,
                lineHeight: 0,
              })}
              aria-label={t('appears_as_report', { category: reportCategory })}
            >
              <i
                className="fa fa-question-circle grey-50 font-size-14"
                aria-hidden="true"
              />
            </button>
          </OverlayTrigger>
        )}
      </div>

      {/* Text field with pill label on border — hidden when AI suggestion is pending */}
      {!pendingAiSuggestion && (
        <div
          className={`error-card__pill-wrapper ${
            showPill ? 'error-card__pill-wrapper--with-pill' : ''
          }`}
        >
          {/* Pill label overlapping the border */}
          {showPill && (
            <span
              data-testid={`source-pill-${category.id}`}
              className="error-card__pill"
            >
              <Badge
                className={css({
                  backgroundColor: pillBg,
                  color: pillColor,
                })}
                value={pillLabel}
              />
            </span>
          )}

          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${inputBorderColor}`,
              borderRadius: '0.25rem',
              backgroundColor: palette.white,
              ':focus-within': {
                borderColor: palette.belizeHole,
              },
            })}
          >
            <input
              type="text"
              data-testid={`custom-message-input-${category.id}`}
              value={customMessage || ''}
              onChange={handleTextChange}
              placeholder={
                isGeneratingAi
                  ? t('generating_with_ai')
                  : t('enter_custom_message')
              }
              disabled={isGeneratingAi}
              className={css({
                flex: 1,
                padding: '0.75rem 1rem',
                border: 'none',
                fontSize: '1rem',
                fontFamily: 'inherit',
                backgroundColor: 'transparent',
                outline: 'none',
                color: palette.grey90,
                '::placeholder': {
                  color: palette.grey50,
                },
                ':disabled': {
                  opacity: 0.5,
                },
              })}
            />
            <div
              className={css({
                display: 'flex',
                gap: '1rem',
                paddingRight: '1rem',
              })}
            >
              {hasChanges && !isGeneratingAi && (
                <button
                  data-testid={`undo-button-${category.id}`}
                  onClick={revert}
                  aria-label={t('revert_to_saved')}
                  className={css({
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    lineHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                  })}
                  title={t('revert_to_saved')}
                >
                  <IconReset width={24} height={24} />
                </button>
              )}
              <button
                data-testid={`ai-wand-${category.id}`}
                onClick={onGenerateAi}
                disabled={isGeneratingAi}
                aria-label={t('ai_wand_label')}
                className={css({
                  background: 'none',
                  border: 'none',
                  cursor: isGeneratingAi ? 'default' : 'pointer',
                  padding: '0',
                  lineHeight: 0,
                  display: 'flex',
                  alignItems: 'center',
                  animationName: isGeneratingAi ? 'wand-pulse' : 'none',
                  animationDuration: '1s',
                  animationIterationCount: 'infinite',
                  animationTimingFunction: 'ease-in-out',
                })}
                title={t('ai_wand_label')}
              >
                <WandMagicSparkles width={24} height={24} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending AI suggestion */}
      {pendingAiSuggestion && (
        <PendingAiSuggestion
          suggestion={pendingAiSuggestion}
          categoryId={category.id}
          onAccept={acceptSuggestion}
          onReject={rejectSuggestion}
        />
      )}

      {/* Available variables */}
      <AvailableVariables variables={category.available_variables} />
    </div>
  )
}

export default ErrorCategoryCard
